import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from 'contentful';
import type { TypeBlockDarkCircleBackgroundSkeleton } from './TypeBlockDarkCircleBackground';
import type { TypeBlockFeaturedCaseStudiesSkeleton } from './TypeBlockFeaturedCaseStudies';
import type { TypeBlockIconBlocksSkeleton } from './TypeBlockIconBlocks';
import type { TypeBlockImageSkeleton } from './TypeBlockImage';
import type { TypeBlockImageWithPartialOverlaySkeleton } from './TypeBlockImageWithPartialOverlay';
import type { TypeBlockIndustryBlocksSkeleton } from './TypeBlockIndustryBlocks';
import type { TypeBlockJobListSkeleton } from './TypeBlockJobList';
import type { TypeBlockLogoGridSkeleton } from './TypeBlockLogoGrid';
import type { TypeBlockNumberedBlocksSkeleton } from './TypeBlockNumberedBlocks';
import type { TypeBlockPageSectionSkeleton } from './TypeBlockPageSection';
import type { TypeBlockPeopleListSkeleton } from './TypeBlockPeopleList';
import type { TypeBlockSlideshowSkeleton } from './TypeBlockSlideshow';
import type { TypeBlockTechnologyBlocksSkeleton } from './TypeBlockTechnologyBlocks';
import type { TypeCompanyTopicSkeleton } from './TypeCompanyTopic';
import type { TypeCtaSimpleSkeleton } from './TypeCtaSimple';
import type { TypeImageWrapperSkeleton } from './TypeImageWrapper';
import type { TypeNt_experienceSkeleton } from './TypeNt_experience';
import type { TypePagePersonSkeleton } from './TypePagePerson';
import type { TypeServiceSkeleton } from './TypeService';
import type { TypeSolutionSkeleton } from './TypeSolution';
import type { TypeTechnologySkeleton } from './TypeTechnology';

export interface TypePageJournalFields {
    internalName: EntryFieldTypes.Symbol;
    slug: EntryFieldTypes.Symbol;
    seoTitle?: EntryFieldTypes.Symbol;
    seoDescription?: EntryFieldTypes.Symbol;
    title: EntryFieldTypes.Symbol;
    authors: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypePagePersonSkeleton>>;
    summary?: EntryFieldTypes.Text;
    heroImage?: EntryFieldTypes.EntryLink<TypeImageWrapperSkeleton>;
    publishDate?: EntryFieldTypes.Date;
    updatedDate?: EntryFieldTypes.Date;
    body?: EntryFieldTypes.RichText;
    companyTopics?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeCompanyTopicSkeleton>>;
    services?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeServiceSkeleton>>;
    solutions?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeSolutionSkeleton>>;
    technologies?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeTechnologySkeleton>>;
    sections?: EntryFieldTypes.Array<
        EntryFieldTypes.EntryLink<
            | TypeBlockDarkCircleBackgroundSkeleton
            | TypeBlockFeaturedCaseStudiesSkeleton
            | TypeBlockIconBlocksSkeleton
            | TypeBlockImageSkeleton
            | TypeBlockImageWithPartialOverlaySkeleton
            | TypeBlockIndustryBlocksSkeleton
            | TypeBlockJobListSkeleton
            | TypeBlockLogoGridSkeleton
            | TypeBlockNumberedBlocksSkeleton
            | TypeBlockPageSectionSkeleton
            | TypeBlockPeopleListSkeleton
            | TypeBlockSlideshowSkeleton
            | TypeBlockTechnologyBlocksSkeleton
            | TypeCtaSimpleSkeleton
        >
    >;
    nt_experiences?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeNt_experienceSkeleton>>;
}

export type TypePageJournalSkeleton = EntrySkeletonType<TypePageJournalFields, 'pageJournal'>;
export type TypePageJournal<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<
    TypePageJournalSkeleton,
    Modifiers,
    Locales
>;

export function isTypePageJournal<Modifiers extends ChainModifiers, Locales extends LocaleCode>(
    entry: Entry<EntrySkeletonType, Modifiers, Locales>
): entry is TypePageJournal<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'pageJournal';
}
